import React, { useState } from 'react';
import './styles.css';
import { getCustomInstructionFieldsByType } from '../util/customInstructionsUtil';
import Modal from '../components/Modal';
import { logEvent } from '../util/apiUtils';

/**
 * Find all "Collect" custom instructions for the selected tests and return a comma-separated string
 * of values, pre-pended with some helper text.
 *
 * The result will be, "Please label and collect devices {comma-separated list}."
 */
const EndoPlusCollectionSummary = ({ orderableItem, gearRequisition, includeInstructions, ...props }) => {
	const [showInstructionsModal, setShowInstructionsModal] = useState(false);

	const labelNumbers = getEndoLabelNumbersToCollect(orderableItem, gearRequisition);

	let summaryTest = '';
	if (labelNumbers?.length) {
		if (labelNumbers.length === 1) {
			summaryTest = `Please label and collect device ${labelNumbers[0]}.`;
		} else if (labelNumbers.length === 2) {
			summaryTest = `Please label and collect devices ${labelNumbers[0]} and ${labelNumbers[1]}.`;
		} else {
			summaryTest = `Please label and collect devices ${labelNumbers
				.slice(0, -1)
				.join(', ')}, and ${labelNumbers.slice(-1)}.`;
		}
	}

	if (!summaryTest) {
		return null;
	}

	return (
		<>
			{summaryTest}
			{includeInstructions && (
				<>
					<span className="mr-5"></span>
					<button
						className="btn btn-link btn-unstyled"
						onClick={() => setShowInstructionsModal(true)}
						{...props}
					>
						View Instructions
					</button>
				</>
			)}
			{showInstructionsModal && (
				<EndoPlusInstructionsModal
					gearRequisition={gearRequisition}
					orderableItem={orderableItem}
					onClose={() => setShowInstructionsModal(false)}
				/>
			)}
		</>
	);
};

/**
 * Display a Modal that contains the Endo+ collection table
 */
const EndoPlusInstructionsModal = ({ orderableItem, gearRequisition, onClose }) => {
	const modalProps = {
		displayProps: {
			modalHeader: { style: { display: 'none' } },
			modalContent: {
				className: 'modal-content modal-scroll',
				style: { borderRadius: '20px', overflow: 'hidden' }
			},
			modalBody: {
				className: 'modal-body',
				style: { color: 'initial' } // Reset color because the modal may be inside something like .alert-info.
			}
		},
		doConfirm: null, // Allow Escape and click outside to close the modal
		modalHidden: onClose
	};

	const doPrint = () => {
		window.print();
		logEvent(
			'{} - Endo+ "Print Collection Instructions" clicked. gearReqId={}',
			'UI_ACTION_PRINT_ENDO_INSTRUCTIONS',
			gearRequisition.id
		);
	};

	return (
		<Modal id="endo-custom-instructions-modal" {...modalProps}>
			<button
				type="button"
				className="close"
				data-dismiss="modal"
				aria-label="Close"
				title="Close"
				style={{ marginTop: '-10px' }}
			>
				<span aria-hidden="true">&times;</span>
			</button>
			<h1 className="mt-0">Collection Instructions</h1>
			<div className="print-action">
				<hr className="mb-5" style={{ borderTopColor: '#eee' }} />
				<button
					type="button"
					onClick={doPrint}
					className="btn btn-link btn-unstyled"
					style={{ marginBottom: '-10px' }}
				>
					<i className="fas fa-print mr-10" aria-hidden="true"></i>Print Instructions
				</button>
			</div>
			<hr style={{ borderTopColor: '#eee' }} />
			<h3 className="bold">What You Will Collect</h3>
			<p>
				<EndoPlusCollectionSummary orderableItem={orderableItem} gearRequisition={gearRequisition} />
			</p>
			<EndoPlusTubeCollectionTable orderableItem={orderableItem} gearRequisition={gearRequisition} />
			<hr className="mt-5" style={{ borderTopColor: '#eee' }} />
			<h3 className="bold">Label Each Device</h3>
			<ol>
				<li>Select the numbered label that corresponds with each needed collection device pictured above.</li>
				<li>Write your date of birth and the timing details on the label.</li>
				<li>Place the completed label on the tube before collecting.</li>
			</ol>
		</Modal>
	);
};

/**
 * Display a table of samples to collect based on the selected tests.
 */
const EndoPlusTubeCollectionTable = ({ orderableItem, gearRequisition }) => {
	// Parse the saved custom instructions and extract the label numbers to display
	const labelNumbers = getEndoLabelNumbersToCollect(orderableItem, gearRequisition);

	const labelDetails = [
		{
			labelNo: 1,
			device: 'Tube',
			image: <img src="/images/sample-collection/endoplus/tube-urine-black-bottom.svg" alt="Black bottom tube" />,
			description: 'Black bottom tube',
			collectionWindow: 'Immediately upon waking',
			collectionType: 'Urine'
		},
		{
			labelNo: 2,
			device: 'Tube',
			image: <img src="/images/sample-collection/endoplus/tube-blue-top-1ml.svg" alt="Blue cap tube" />,
			description: 'Blue cap tube',
			collectionWindow: 'Immediately upon waking',
			collectionType: 'Saliva'
		},
		{
			labelNo: 3,
			device: 'Tube',
			image: <img src="/images/sample-collection/endoplus/tube-blue-top-1ml.svg" alt="Blue cap tube" />,
			description: 'Blue cap tube',
			collectionWindow: '30 minutes after waking',
			collectionType: 'Saliva'
		},
		{
			labelNo: 4,
			device: 'Tube',
			image: <img src="/images/sample-collection/endoplus/tube-grey-top-3ml.svg" alt="Grey cap tube" />,
			description: 'Grey cap tube',
			collectionWindow: '7:00am to 9:00am',
			collectionType: 'Saliva'
		},
		{
			labelNo: 5,
			device: 'Tube',
			image: <img src="/images/sample-collection/endoplus/tube-grey-top-3ml.svg" alt="Grey cap tube" />,
			description: 'Grey cap tube',
			collectionWindow: '11:00am to 1:00pm',
			collectionType: 'Saliva'
		},
		{
			labelNo: 6,
			device: 'Tube',
			image: <img src="/images/sample-collection/endoplus/tube-grey-top-3ml.svg" alt="Grey cap tube" />,
			description: 'Grey cap tube',
			collectionWindow: '3:00pm to 5:00pm',
			collectionType: 'Saliva'
		},
		{
			labelNo: 7,
			device: 'Tube',
			image: <img src="/images/sample-collection/endoplus/tube-grey-top-3ml.svg" alt="Grey cap tube" />,
			description: 'Grey cap tube',
			collectionWindow: '10:00pm to 12:00am',
			collectionType: 'Saliva'
		},
		{
			labelNo: 8,
			device: 'Tube',
			image: <img src="/images/sample-collection/endoplus/tube-grey-top-3ml.svg" alt="Grey cap tube" />,
			description: 'Grey cap tube',
			collectionWindow: '2:30am to 3:30am',
			collectionType: 'Saliva'
		},
		{
			labelNo: 9,
			device: 'Swab',
			image: <img src="/images/sample-collection/endoplus/swabs.svg" alt="Cotton swabs" />,
			description: 'Cotton swabs',
			collectionWindow: 'Immediately upon waking',
			collectionType: 'Buccal'
		},
		{
			labelNo: 10,
			device: 'Tube',
			image: <img src="/images/sample-collection/endoplus/tube-grey-top-serum.svg" alt="Grey cap" />,
			description: 'Healthcare provider will label and collect',
			collectionType: 'Serum'
		},
		{
			labelNo: 11,
			device: 'Tube',
			image: <img src="/images/sample-collection/endoplus/tube-grey-top-serum.svg" alt="Grey cap" />,
			description: 'Healthcare provider will label and collect',
			collectionType: 'Serum'
		}
	];

	return (
		<div className="sample-collection">
			<table className="table tube-collection-table">
				<thead>
					<tr>
						<th className="vertical">Needed</th>
						<th className="vertical">Label #</th>
						<th colSpan={2}>Device Description</th>
						<th>Collection Window</th>
						<th>Type</th>
					</tr>
				</thead>
				<tbody>
					{labelDetails.map((details) => {
						const neededIcon = labelNumbers?.includes(details.labelNo) ? (
							<i className="fas fa-check text-success" aria-hidden="true" title="Collection Required"></i>
						) : (
							<i className="fas fa-times text-danger" aria-hidden="true" title="Not Needed"></i>
						);

						return (
							<tr key={details.labelNo} id={`label-${details.labelNo}`}>
								<td>{neededIcon}</td>
								<td>{details.labelNo}</td>
								<td className="device-img">{details.image}</td>
								{!details.collectionWindow ? (
									<td colSpan={2} className="text-left">
										{details.description}
									</td>
								) : (
									<>
										<td className="text-left">{details.description}</td>
										<td className="text-left">{details.collectionWindow}</td>
									</>
								)}
								<td>{details.collectionType}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

/**
 * Parse all instructions that begin with "Collect|endo+" and return a sorted list of any numbers found.
 */
const getEndoLabelNumbersToCollect = (orderableItem, gearRequisition) => {
	let labelNumbers = getCustomInstructionFieldsByType(orderableItem, gearRequisition, 'Collect|endo+')
		.map((field) => field.value)
		.flatMap((instruction) => instruction.match(/\d+/g)) // Extract all numbers from all instructions and flatten into a 1D array
		.map(Number)
		.filter(Boolean); // Is not empty or 0
	
	/**
	 * SCR-8352: Update required Serum tubes based on selected combinations.
	 * 1 group -> collect #10, 2+ groups -> collect #10 & # 11.
	 * 
	 * "Serum, Hormones" & "Thyroid Hormones" are both configured with #10 so use the count of 10's to know how many groups are selected.
	 * All Serum add-ons are configured with #11 because it doesn't matter how many are selected, it's still just counted as 1 group.
	 */
	let serumGroupCount = labelNumbers.filter(label => label === 10).length;
	if (!!labelNumbers.find(label => label === 11)) {
		serumGroupCount++;
	}

	if (serumGroupCount === 1) {
		labelNumbers = labelNumbers.filter(label => label !== 11)
		labelNumbers.push(10);
	} else if (serumGroupCount > 1) {
		labelNumbers.push(10);
		labelNumbers.push(11);
	}

	labelNumbers = labelNumbers.sort((a, b) => a - b);

	// Remove duplicates
	return Array.from(new Set(labelNumbers));
};

/**
 * Return true if any selected tests have a "Collect|endo+|" custom instruction.
 */
const hasEndoPlusCustomInstructions = (orderableItem, gearRequisition) => {
	return getEndoLabelNumbersToCollect(orderableItem, gearRequisition)?.length > 0;
};

export { EndoPlusInstructionsModal, getEndoLabelNumbersToCollect, hasEndoPlusCustomInstructions };
export default EndoPlusCollectionSummary;
